import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { BRIEF_DOCK_LAYOUT_ID, CHAT_DOCK_SPRING } from '@/common/motion/chatDockMotion';
import type { DockRect } from '@/common/motion/chatDockMotion';

interface BriefDockFlightProps {
  /** Resting rect of the Brief panel (where the surface sits during the flight). */
  rect: DockRect;
  /** 'undocking' = bell → panel (open, grow); 'docking' = panel → bell (close, shrink). */
  mode: 'docking' | 'undocking';
  /** Undocking only: the flight landed on the panel — settle to 'open'. */
  onArrived: () => void;
}

/**
 * The traveling "brief surface" of the Daily Brief dock/undock shared-element
 * morph.
 *
 * Shares `layoutId` with the header bell (HeaderBriefLauncher): when this mounts
 * as the bell unmounts (open), or unmounts as the bell mounts (close),
 * framer-motion FLIPs the surface between the panel rect and the 34×34 header
 * bell slot — so the bell grows into the Brief and the Brief shrinks back into
 * the bell. Mirrors ChatDockFlight.
 *
 * Portaled to <body> with position:fixed so the flight renders above the fixed
 * app header (z-50) and is never clipped by the chat panel's overflow/stacking
 * contexts (an in-grid traveler would vanish at the panel edge). Purely visual:
 * pointer-events none, aria-hidden.
 */
export function BriefDockFlight({ rect, mode, onArrived }: BriefDockFlightProps) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      className={`brief-dock-flight brief-dock-flight-${mode}`}
      layoutId={BRIEF_DOCK_LAYOUT_ID}
      transition={{
        // Reuses the chat dock's spring (600/36/0.85) VERBATIM so the Brief
        // bell↔panel morph opens/closes at the exact same ~189ms tactile pace
        // as the chat dock — identical to ChatDockFlight.
        ...CHAT_DOCK_SPRING,
        // Fade-in (closing) syncs with the panel content fade beneath it — a
        // slower glass fade leaves a half-ghost over an empty card.
        opacity: { duration: 0.06, ease: 'easeOut' },
      }}
      // Fade in ONLY on the close direction ('docking': the glass mounts fresh
      // over the fading panel). On OPEN ('undocking') the surface inherits the
      // shared-layoutId crossfade from the exiting bell — fading it from 0 there
      // leaves a blank gap right as the bell vanishes (a visible blink). Mirrors
      // ChatDockFlight's initial={mode === 'docking' ? {opacity: 0} : false}.
      initial={mode === 'docking' ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      onLayoutAnimationComplete={mode === 'undocking' ? onArrived : undefined}
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        zIndex: 60,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    >
      {/* Glass lives on a child: WebKit can drop backdrop-filter when it shares
          an element with mask-image, so the mask stays on the traveler and the
          blur on this inner surface. */}
      <div className="brief-dock-flight-surface" />
    </motion.div>,
    document.body,
  );
}
